import React, { useCallback, useMemo, useState } from 'react';
import Loader from 'components/Loader';
import AlertModal from 'components/AlertModal';
import { useTranslation } from 'hooks/useTranslation';
import { PAYPAL_SCREEN_KEYS } from './translationKeys';
import { useRefreshPaymentMethods } from '../../utils/useRefreshPaymentMethods';
import { PayPalLinkAccountScreen } from './screens/PayPalLinkAccountScreen';
import { PayPalAccountsGridScreen } from './screens/PayPalAccountsGridScreen';
import { PayPalAccountDetailScreen } from './screens/PayPalAccountDetailScreen';
import './style.scss';

// Clon de containers/Payment/PayPal adaptado al CRUD de vinculación QR.
//
// La diferencia estructural con el original: en Payment el paso vive en
// `location.state.screen` y se mueve con goToScreen. Aquí NO — `qrUserData` /
// `user_token` viajan solo en `location.state`, y cualquier navigate que no los
// reenvíe rompe el flujo. El paso es estado local, igual que la sub-máquina de
// TDCScreen.
//
// Alcance: solo el camino popup del vault (onApprove corre en la misma página).
// El camino de redirect de página completa queda fuera.
export const VIEWS = {
  LINK: 'link',
  GRID: 'grid',
  DETAIL: 'detail',
};

const normalizeAccounts = accounts => {
  if (Array.isArray(accounts)) return accounts;
  if (!accounts) return [];
  return [accounts];
};

export const PayPalScreen = ({
  accounts = [],
  sessionUser,
  isMobileFlow = false,
  transactionParams,
  deviceParams,
  code,
  paymentMethodId,
  displayName,
  onAddSuccess,
  onAccountDeleted,
  onPaymentMethodsRefreshed,
}) => {
  const { translationMapper } = useTranslation();
  const translatedTexts = useMemo(() => translationMapper(PAYPAL_SCREEN_KEYS), [
    translationMapper,
  ]);

  const paypalAccounts = normalizeAccounts(accounts);

  const [view, setView] = useState(
    paypalAccounts.length > 0 ? VIEWS.GRID : VIEWS.LINK,
  );
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [fromAccountsGrid, setFromAccountsGrid] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const goToGrid = () => {
    setSelectedAccount(null);
    setView(VIEWS.GRID);
  };

  const handleAddAccount = () => {
    setFromAccountsGrid(true);
    setView(VIEWS.LINK);
  };

  const handleSelectAccount = account => {
    setSelectedAccount(account);
    setView(VIEWS.DETAIL);
  };

  const { isRefreshing, refreshPaymentMethods } = useRefreshPaymentMethods({
    userToken: sessionUser?.user_token,
    deviceParams,
    onRefreshed: onPaymentMethodsRefreshed,
  });

  // El hook solo señala que sync-client resolvió; no llega la cuenta. Payment
  // usa sessionStorage['paypal_vault_success'] para esto, aquí basta el estado
  // local. El ajuste local ya se aplicó arriba: si el refetch falla, no hay
  // nada que revertir, se queda como está.
  const handleVaultSuccess = useCallback(
    () => {
      onAddSuccess?.();

      setSelectedAccount(null);
      setFromAccountsGrid(false);
      setView(VIEWS.GRID);
      setShowSuccessModal(true);
      refreshPaymentMethods();
    },
    [onAddSuccess, refreshPaymentMethods],
  );

  const handleSdkError = useCallback(() => setShowErrorModal(true), []);

  // Ya no cierra la sesión de vinculación: agregar solo actualiza la lista. El
  // cierre queda exclusivamente detrás de la selección explícita del método
  // (PayPalAccountDetailScreen -> useSelectPaymentMethod).
  const handleSuccessAutoClose = () => setShowSuccessModal(false);

  const handleAccountDeleted = async account => {
    // La vista se decide con la lista previa menos la cuenta borrada: es lo que
    // el refresco va a devolver, y el prop `accounts` aún no ha llegado.
    const remaining = paypalAccounts.filter(
      item =>
        (item?.accountId || item?.email) !==
        (account?.accountId || account?.email),
    );

    await refreshPaymentMethods(account, onAccountDeleted);

    setSelectedAccount(null);

    if (remaining.length > 0) {
      setView(VIEWS.GRID);
      return;
    }

    setFromAccountsGrid(false);
    setView(VIEWS.LINK);
  };

  return (
    <>
      {view === VIEWS.LINK && (
        <PayPalLinkAccountScreen
          fromAccountsGrid={fromAccountsGrid}
          userId={sessionUser?.user_id}
          userToken={sessionUser?.user_token}
          transactionParams={transactionParams}
          onVaultSuccess={handleVaultSuccess}
          onError={handleSdkError}
          onBack={goToGrid}
        />
      )}

      {view === VIEWS.GRID && (
        <PayPalAccountsGridScreen
          accounts={paypalAccounts}
          showSuccessModal={showSuccessModal}
          onSuccessClose={() => setShowSuccessModal(false)}
          onSuccessAutoClose={handleSuccessAutoClose}
          onAddAccount={handleAddAccount}
          onSelectAccount={handleSelectAccount}
        />
      )}

      {view === VIEWS.DETAIL && selectedAccount && (
        <PayPalAccountDetailScreen
          account={selectedAccount}
          userToken={sessionUser?.user_token}
          paymentMethodId={paymentMethodId}
          displayName={displayName}
          sessionUser={sessionUser}
          isMobileFlow={isMobileFlow}
          deviceParams={deviceParams}
          code={code}
          onBack={goToGrid}
          onDeleted={handleAccountDeleted}
        />
      )}

      {isRefreshing && (
        <div className="block-screen_opacity">
          <Loader active />
        </div>
      )}

      <AlertModal
        open={showErrorModal}
        type="error"
        onClose={() => setShowErrorModal(false)}
        messageByLines={{
          line1: translatedTexts.ERROR_TEXT_1,
          line2: translatedTexts.ERROR_TEXT_2,
          line3: translatedTexts.ERROR_TEXT_3,
        }}
      />
    </>
  );
};

export default PayPalScreen;
